// Simple status utilities - workflow configuration comes from API
import { getStatusColorConfig } from './statusColors';

// Format status for display - fallback when no workflow config
const formatStatusFallback = (status: string): string => {
  // Format the status string by replacing underscores and capitalizing
  return status
    .split('_')
    .map(word => {
      // Special case for 'ai' to become 'AI'
      if (word.toLowerCase() === 'ai') {
        return 'AI';
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

// Get status label from workflow configuration or use fallback formatting
export const getStatusLabel = (status: string | undefined, workflow?: any): string => {
  if (!status) {
    return 'Unknown';
  }

  // Try to get the name from workflow configuration
  if (workflow?.ui) {
    const uiData = workflow.ui as any;
    const statusConfig = uiData.status;

    if (statusConfig && statusConfig[status] && statusConfig[status].name) {
      return statusConfig[status].name;
    }
  }

  // Fallback to formatting the status key
  return formatStatusFallback(status);
};

/**
 * Get status label from status configuration (schema service).
 * This is the new API that accepts statusConfig directly instead of workflow.
 * @param status - The status key
 * @param statusConfig - The status configuration from schema service
 */
export const getStatusLabelFromConfig = (
  status: string | undefined,
  statusConfig?: Record<string, any>,
): string => {
  if (!status) {
    return 'Unknown';
  }

  // Try to get the name from status configuration
  if (statusConfig && statusConfig[status] && statusConfig[status].name) {
    return statusConfig[status].name;
  }

  // Fallback to formatting the status key
  return formatStatusFallback(status);
};

// Status color and label type
export interface StatusColorWithLabel {
  bg: string;
  text: string;
  border: string;
  label?: string; // Optional custom label for the status
}

// Default fallback color for states not in workflow configuration
const DEFAULT_STATUS_COLOR: StatusColorWithLabel = {
  bg: 'bg-muted',
  text: 'text-foreground',
  border: 'border',
};

/**
 * Extract status color and label from status configuration.
 * @param status - The status key
 * @param statusConfig - The status configuration object
 */
const getStatusColorFromConfig = (
  status: string,
  statusConfig: Record<string, any>,
): StatusColorWithLabel | null => {
  if (!statusConfig) {
    return null;
  }

  // If status exists in config but doesn't have the specific state, return default
  if (!statusConfig[status]) {
    return DEFAULT_STATUS_COLOR;
  }

  const stateConfig = statusConfig[status];
  const color = stateConfig.color;

  if (!color) {
    return DEFAULT_STATUS_COLOR;
  }

  return {
    bg: color.bg || DEFAULT_STATUS_COLOR.bg,
    text: color.text || DEFAULT_STATUS_COLOR.text,
    border: color.border || DEFAULT_STATUS_COLOR.border,
    label: stateConfig.label || stateConfig.name, // Use label or name from config
  };
};

// Extract status color and label from workflow configuration
const getWorkflowStatusColor = (status: string, workflow: any): StatusColorWithLabel | null => {
  const uiData = workflow.ui as any;
  const statusConfig = uiData.status;

  return getStatusColorFromConfig(status, statusConfig);
};

// Helper to get color from workflow or design-defined colors
const getColorFromWorkflowOrDesign = (
  status: string,
  workflow: any,
): StatusColorWithLabel | null => {
  // Check if workflow has ui.status configuration
  const hasWorkflowStatusConfig = workflow.ui?.status && Object.keys(workflow.ui.status).length > 0;

  if (hasWorkflowStatusConfig) {
    // Workflow has status config - use it exclusively
    const workflowColor = getWorkflowStatusColor(status, workflow);
    if (workflowColor) {
      return workflowColor;
    }
    return DEFAULT_STATUS_COLOR;
  }

  // No workflow status config - use design-defined colors
  const designColor = getStatusColorConfig(status);
  return designColor || DEFAULT_STATUS_COLOR;
};

// Get status color and label from workflow configuration or use design-defined colors
// Returns null when workflow is loading to hide the badge
export const getStatusColor = (
  status: string | undefined,
  workflow?: any,
  activeCallId?: string,
): StatusColorWithLabel | null => {
  if (!status) {
    return DEFAULT_STATUS_COLOR;
  }

  // Get base color configuration
  let colorConfig: StatusColorWithLabel | null;

  // If workflow is provided, try to use workflow configuration
  if (workflow !== undefined && workflow !== null) {
    colorConfig = getColorFromWorkflowOrDesign(status, workflow);
  } else {
    // No workflow provided - use design-defined colors as fallback
    colorConfig = getStatusColorConfig(status);
  }

  if (!colorConfig) {
    colorConfig = DEFAULT_STATUS_COLOR;
  }

  // Override label based on active_call_id for call states
  if (activeCallId) {
    if (status === 'ai_call') {
      return {
        ...colorConfig,
        label: 'AI Call In-Progress',
      };
    } else if (status === 'human_call') {
      return {
        ...colorConfig,
        label: 'Human Call In-Progress',
      };
    }
  }

  return colorConfig;
};

/**
 * Get status color and label from status configuration (schema service).
 * This is the new API that accepts statusConfig directly instead of workflow.
 * @param status - The status key
 * @param statusConfig - The status configuration from schema service
 * @param activeCallId - Optional active call ID for in-progress label override
 */
export const getStatusColorWithConfig = (
  status: string | undefined,
  statusConfig?: Record<string, any>,
  activeCallId?: string,
): StatusColorWithLabel | null => {
  if (!status) {
    return DEFAULT_STATUS_COLOR;
  }

  // Get base color configuration
  let colorConfig: StatusColorWithLabel | null;

  // If statusConfig is provided, use it
  if (statusConfig && Object.keys(statusConfig).length > 0) {
    colorConfig = getStatusColorFromConfig(status, statusConfig);
  } else {
    // No statusConfig provided - use design-defined colors as fallback
    colorConfig = getStatusColorConfig(status);
  }

  if (!colorConfig) {
    colorConfig = DEFAULT_STATUS_COLOR;
  }

  // Override label based on active_call_id for call states
  if (activeCallId) {
    if (status === 'ai_call') {
      return {
        ...colorConfig,
        label: 'AI Call In-Progress',
      };
    } else if (status === 'human_call') {
      return {
        ...colorConfig,
        label: 'Human Call In-Progress',
      };
    }
  }

  return colorConfig;
};

// Simple action configuration - just return basic actions for now
export const getStatusActions = (_status: string | undefined) => {
  return {
    primary: {
      label: 'View Details',
      action: 'view_details',
    },
  };
};

// Always show footer actions
export const shouldShowFooterActions = (): boolean => {
  return true;
};

// Header badge - not needed for simplified version
export const getHeaderBadge = () => {
  return null;
};
